import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import {
  Car, FileText, Phone, Mail, MapPin, Loader2, AlertTriangle, CheckCircle2,
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

function fmtDate(iso) {
  if (!iso) return "";
  return new Date(iso).toLocaleString("fr-CH", {
    day: "2-digit", month: "long", year: "numeric", hour: "2-digit", minute: "2-digit",
  });
}

export default function PublicPret() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    axios.get(`${BACKEND_URL}/api/public/pret/${token}`)
      .then((r) => setData(r.data))
      .catch((e) => setError(e.response?.data?.detail || "Erreur"))
      .finally(() => setLoading(false));
  }, [token]);

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-[#f0f9f4] to-white"><Loader2 className="w-8 h-8 animate-spin text-[#52a675]" /></div>;
  }
  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center p-6 bg-gradient-to-br from-orange-50 to-white">
        <div className="max-w-md text-center">
          <AlertTriangle className="w-12 h-12 mx-auto text-orange-600 mb-3" />
          <h1 className="text-xl font-bold mb-2">Lien indisponible</h1>
          <p className="text-gray-600">{error}</p>
        </div>
      </div>
    );
  }

  const dep = data.depose || {};
  const ret = data.retour;
  return (
    <div className="min-h-screen bg-gradient-to-br from-[#f0f9f4]/40 to-white pb-16" data-testid="public-pret-page">
      <header className="bg-gradient-to-r from-[#2d5f3f] to-[#52a675] text-white py-5 px-4 shadow-md">
        <div className="max-w-3xl mx-auto flex items-center gap-3">
          <div className="w-12 h-12 rounded-md bg-white/15 flex items-center justify-center"><Car className="w-6 h-6" /></div>
          <div className="flex-1 min-w-0">
            <div className="text-xs uppercase tracking-wider opacity-80">Contrat n°{data.numero}</div>
            <div className="font-bold text-lg truncate">{data.garage.name}</div>
          </div>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 pt-6 space-y-4">
        <section className="bg-white rounded-xl shadow-sm border border-gray-100 p-5">
          <h1 className="text-xl font-bold mb-1">Bonjour {data.conducteur_nom},</h1>
          <p className="text-sm text-gray-600 mb-4">
            Voici votre contrat de prêt véhicule chez {data.garage.name}. Ce document fait foi de l'état des lieux.
          </p>
          <div className="bg-gray-50 rounded-lg p-3 flex items-center gap-3 mb-3">
            <Car className="w-5 h-5 text-[#52a675]" />
            <div className="text-sm">
              <div className="font-semibold">{[data.flotte.marque, data.flotte.modele].filter(Boolean).join(" ")}</div>
              <div className="text-xs text-gray-500">{data.flotte.plaque}{data.flotte.couleur && ` · ${data.flotte.couleur}`}</div>
            </div>
          </div>
          <div className="text-sm space-y-1.5 border-t border-gray-100 pt-3">
            <Row label="Dépose" value={fmtDate(dep.date_heure)} />
            <Row label="Km au départ" value={`${dep.km_depart?.toLocaleString("fr-CH")} km`} />
            <Row label="Carburant" value={dep.niveau_carburant} />
            <Row label="Retour prévu" value={fmtDate(dep.date_retour_prevu)} />
            <Row label="Tarif" value={dep.courtoisie ? "Prêt courtoisie (gratuit)" : `${dep.tarif_jour?.toFixed(2)} CHF/jour`} />
          </div>

          {ret && (
            <div className="mt-4 pt-4 border-t border-gray-100">
              <div className="text-xs uppercase tracking-wider font-bold text-[#52a675] mb-2">État au retour ✓</div>
              <div className="text-sm space-y-1.5">
                <Row label="Retour effectué" value={fmtDate(ret.date_heure)} />
                <Row label="Km retour" value={`${ret.km_retour?.toLocaleString("fr-CH")} km`} />
                <Row label="Durée" value={`${ret.duree_jours} jour(s)`} />
                <Row label="Total facturé" value={`${ret.total_facture?.toFixed(2)} CHF`} highlight />
              </div>
            </div>
          )}

          <a href={`${BACKEND_URL}${data.pdf_url}`} target="_blank" rel="noreferrer" className="mt-4 w-full inline-flex items-center justify-center gap-2 py-2.5 rounded-lg border border-gray-200 text-sm font-semibold hover:bg-gray-50" data-testid="public-pret-pdf">
            <FileText className="w-4 h-4" /> Télécharger le contrat (PDF)
          </a>
          {data.retour_pdf_url && (
            <a href={`${BACKEND_URL}${data.retour_pdf_url}`} target="_blank" rel="noreferrer" className="mt-2 w-full inline-flex items-center justify-center gap-2 py-2.5 rounded-lg border border-[#52a675]/30 bg-[#52a675]/5 text-sm font-semibold text-[#3d8a5c] hover:bg-[#52a675]/10" data-testid="public-pret-retour-pdf">
              <CheckCircle2 className="w-4 h-4" /> Bordereau de retour (PDF)
            </a>
          )}
        </section>

        <section className="text-center text-xs text-gray-500 pt-4 space-y-1">
          <div className="font-semibold text-gray-700">{data.garage.name}</div>
          <div className="flex items-center justify-center gap-1"><MapPin className="w-3 h-3" /> {data.garage.adresse}, {data.garage.npa} {data.garage.localite}</div>
          <div className="flex items-center justify-center gap-3 mt-1">
            <a href={`tel:${data.garage.tel}`} className="inline-flex items-center gap-1 hover:text-[#52a675]"><Phone className="w-3 h-3" /> {data.garage.tel}</a>
            <a href={`mailto:${data.garage.email}`} className="inline-flex items-center gap-1 hover:text-[#52a675]"><Mail className="w-3 h-3" /> {data.garage.email}</a>
          </div>
        </section>
      </main>
    </div>
  );
}

function Row({ label, value, highlight }) {
  return (
    <div className="flex justify-between">
      <span className="text-gray-500">{label}</span>
      <span className={highlight ? "font-bold text-[#2d5f3f]" : "font-semibold"}>{value}</span>
    </div>
  );
}
